import type { Point } from "../domain/drill";

export function initialBarrierStart(center: Point, length = 52): Point {
  return { x: center.x - length / 2, y: center.y };
}

export function rotationFromCenter(center: Point, pointer: Point) {
  return (
    (Math.atan2(pointer.y - center.y, pointer.x - center.x) * 180) / Math.PI
  );
}

export function snapRotation(
  rotation: number,
  threshold = 7,
): { rotation: number; axis: "horizontal" | "vertical" | undefined } {
  const nearest = Math.round(rotation / 90) * 90;
  return Math.abs(rotation - nearest) <= threshold
    ? { rotation: nearest, axis: nearest % 180 === 0 ? "horizontal" : "vertical" }
    : { rotation, axis: undefined };
}

export function barrierPlacementFromCenter(
  center: Point,
  pointer: Point,
  initialLength = 52,
) {
  const dx = pointer.x - center.x;
  const dy = pointer.y - center.y;
  const distance = Math.hypot(dx, dy);
  if (!distance)
    return {
      point: center,
      rotation: 0,
      length: initialLength,
    };
  const half = initialLength / 2;
  const unitX = dx / distance;
  const unitY = dy / distance;
  const snapped = snapRotation((Math.atan2(dy, dx) * 180) / Math.PI);
  if (snapped.axis) {
    const angle = (snapped.rotation * Math.PI) / 180;
    const snapX = Math.cos(angle);
    const snapY = Math.sin(angle);
    const anchor = {
      x: center.x - snapX * half,
      y: center.y - snapY * half,
    };
    const projectedDrag = Math.max(0, dx * snapX + dy * snapY);
    const length = Math.max(18, initialLength + projectedDrag);
    return {
      point: {
        x: anchor.x + (snapX * length) / 2,
        y: anchor.y + (snapY * length) / 2,
      },
      rotation: snapped.rotation,
      length,
    };
  }
  const anchor = {
    x: center.x - unitX * half,
    y: center.y - unitY * half,
  };
  return barrierPlacement(anchor, pointer, 18, half);
}

export function barrierPlacement(
  origin: Point,
  pointer: Point,
  minimumLength = 18,
  dragOffset = 0,
) {
  const dx = pointer.x - origin.x;
  const dy = pointer.y - origin.y;
  const distance = Math.hypot(dx, dy);
  const rotation = (Math.atan2(dy, dx) * 180) / Math.PI;
  const length = Math.max(minimumLength, distance + dragOffset);
  const unitX = distance ? dx / distance : 1;
  const unitY = distance ? dy / distance : 0;

  return {
    point: {
      x: origin.x + (unitX * length) / 2,
      y: origin.y + (unitY * length) / 2,
    },
    rotation,
    length,
  };
}
